'use client';

import { useState, useCallback } from 'react';
import { useUnit } from 'effector-react';
import { Button, ButtonSize, ButtonType } from '@nx-frontend/shared/ui/button';
import { closeModalEvent } from '@nx-frontend/shared/ui/modal';
import { useToast } from '@nx-frontend/shared/ui/toast';
import { addSubscriptionAddon } from '@nx-frontend/shared/api';
import { $addonAutomationPrice } from '@nx-frontend/shared/store/addon-automation-price';
import { $billingInfo } from '@nx-frontend/shared/store/billing-info/billing-info';
import { $currentOrganization } from '@nx-frontend/shared/store/current-organization/current-organization';
import { $user } from '@nx-frontend/shared/store/user';
import { BillingAddonCode, PurchaseAutomationBillingPopupPayload, PopupType } from '@nx-frontend/shared/types';
import { OpenModalParams } from '@nx-frontend/shared/types';
import { mapPlanToSubscription } from '@nx-frontend/shared/utils';

export interface GetOpenPurchaseAutomationBillingPopupProps {
  openModal: (params: OpenModalParams) => void;
  payload?: PurchaseAutomationBillingPopupPayload;
}

export const getOpenPurchaseAutomationBillingPopup = ({
  openModal,
  payload,
}: GetOpenPurchaseAutomationBillingPopupProps) => {
  openModal({
    content: PopupType.PURCHASE_AUTOMATION_BILLING,
    showCloseButton: false,
    modalContainerStyles: 'rounded-lg',
    payload,
  });
};

export function PurchaseAutomationBillingPopup() {
  const [isPurchased, setIsPurchased] = useState(false);

  const [user, billingInfo, currentOrganization, addonAutomationPrice] = useUnit([
    $user,
    $billingInfo,
    $currentOrganization,
    $addonAutomationPrice,
  ]);

  const { toast } = useToast();
  const [closeModal] = useUnit([closeModalEvent]);

  const confirmClick = useCallback(async () => {
    setIsPurchased(true);
    addSubscriptionAddon({
      userId: user?.id,
      subscriptionId: billingInfo?.subscriptionId,
      orgId: currentOrganization?.globalId,
      code: BillingAddonCode.AUTOMATION,
      quantity: addonAutomationPrice.quantity,
    })
      .then(() => {
        toast({ title: 'FuseBase Automation works for you now!' });
        closeModal();
      })
      .catch((err) => {
        setIsPurchased(false);
        console.error(`Fail to add subscription Automation addon`, err);
      });
  }, [closeModal, toast, user, billingInfo, currentOrganization, setIsPurchased, addonAutomationPrice.quantity]);

  const closeClick = useCallback(async () => {
    closeModal();
  }, [closeModal]);

  const getInfoWithPrices = () => {
    let info =
      'One more step: an extra $#totalForAddon# will be charged. Note that your FuseBase automation subscription coincides with your current #subscriptionType# subscription. Your total payment next time will be $#totalForNextPeriod#*.';

    const { type: subscriptionType } = mapPlanToSubscription(billingInfo);
    const prices = {
      payAmountNow: addonAutomationPrice?.payAmountNow || 0,
      subscriptionTotal: addonAutomationPrice?.subscriptionTotal || 0,
      totalForNextPeriod: addonAutomationPrice?.totalForNextPeriod || 0,
    };

    info = info.replace('#totalForAddon#', prices.payAmountNow.toString());
    info = info.replace('#totalForNextPeriod#', prices.totalForNextPeriod.toString());
    info = info.replace('#subscriptionType#', subscriptionType);

    return info;
  };

  return (
    <div
      data-cy="unlock-automation-billing-popup"
      className="px-2 py-[0] w-[552px] min-w-[552px] min-h-[592px] relative nimbus-popup-content mx-1"
    >
      <div className="flex flex-col relative">
        <div className="items-center basis-full w-full mb-[16px]">
          <img
            src="/assets/img/unlock-ai-billing.png"
            height={331}
            className="w-full min-h-[331px] rounded-lg"
            alt=""
          />
        </div>
        <div className="flex flex-col mb-[17px]">
          <div className="text-2xl font-semibold mb-[14px]">Your add-on is almost ready</div>
          <div className="mb-[10px] text-baseline font-normal">{getInfoWithPrices()}</div>
          <div className="mb-[12px] text-xs text-black/30">* Does not include coupons or discounts.</div>
        </div>
        <div className="actions flex flex-row align-middle justify-end items-center">
          <div className="mr-[16px]">
            <Button size={ButtonSize.SM} type={ButtonType.BASIC} onClick={closeClick}>
              Cancel
            </Button>
          </div>
          <div>
            <Button
              type={ButtonType.FLAT}
              onClick={confirmClick}
              bg="bg-[#002730] hover:bg-[#002730] active:bg-[#002730]"
              radius="rounded"
              disabled={isPurchased}
              data-cy="unlock-automation-billing-btn"
            >
              Continue
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
